/**
 * App navigation helpers: canonical paths for Quill's core objects, the in-app document
 * workspace (`/documents/:documentId/edit`, CONTRACTS-W2 §A), template editors, legacy
 * `/editor?...` redirects, a per-user "recent documents" list, the unsaved-work flush
 * registry and the quick-create request.
 *
 * Pages call these instead of building URLs or using `window.open('/editor?...')`:
 *   import { openDocumentEditor } from '../utils/navigation';
 *   openDocumentEditor(navigate, { documentId, projectId });
 */

/** localStorage key prefix for the recent documents list (suffixed with user:account; best-effort). */
export const RECENT_DOCUMENTS_STORAGE_KEY = 'quill.recentDocuments.v1';
/** Max entries kept in the recent documents list. */
export const RECENT_DOCUMENTS_LIMIT = 12;
/** Event fired on window when the recent documents list changes. */
export const RECENT_DOCUMENTS_EVENT = 'quill:recent-documents';

/**
 * Build a query string from an object, skipping empty values.
 * @param {Record<string, string|number|boolean|null|undefined>} params - Query values.
 * @returns {string} `?a=1&b=2` or ''.
 */
export function buildQuery(params) {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '' || value === false) return;
    search.set(key, value === true ? '1' : String(value));
  });
  const text = search.toString();
  return text ? `?${text}` : '';
}

/**
 * Path of the in-app document workspace.
 * @param {string} documentId - Document id.
 * @param {{projectId?: string|null, templateId?: string|null}} [options] - Extra context kept in the query.
 * @returns {string} Path such as `/documents/<id>/edit?projectId=<p>`.
 */
export function documentEditorPath(documentId, { projectId, templateId } = {}) {
  if (!documentId) throw new Error('documentEditorPath: documentId is required');
  return `/documents/${encodeURIComponent(documentId)}/edit${buildQuery({ projectId, templateId })}`;
}

/**
 * Open a browser tab for an app path. Called synchronously from a click handler so popup
 * blockers allow it. The opener is kept (same origin) so the editor tab can notify the list
 * it was opened from when it saves.
 * @param {string} path - App path.
 * @returns {Window|null} The new window, or null when blocked.
 */
export function openInNewTab(path) {
  if (typeof window === 'undefined') return null;
  const url = new URL(path, window.location.origin).toString();
  return window.open(url, '_blank');
}

/**
 * Open a document in the in-app workspace (default) or in a new tab.
 * @param {Function} navigate - react-router `navigate`.
 * @param {{documentId: string, projectId?: string|null, templateId?: string|null, newTab?: boolean, replace?: boolean, title?: string}} options - Target.
 * @returns {string} The path that was opened.
 */
export function openDocumentEditor(navigate, { documentId, projectId, templateId, newTab = false, replace = false, title } = {}) {
  const path = documentEditorPath(documentId, { projectId, templateId });
  if (title) recordRecentDocument({ id: documentId, name: title, projectId: projectId || null });
  if (newTab) {
    openInNewTab(path);
    return path;
  }
  navigate(path, { replace });
  return path;
}

/**
 * Path of a template editor.
 * @param {{templateId?: string, kind?: 'document'|'powerpoint'|'powerpoint_rich'|'powerpoint-rich'}} options - Template id (or 'new') and kind.
 * @returns {string} Editor path.
 */
export function templateEditorPath({ templateId = 'new', kind = 'document' } = {}) {
  const isDeck = kind === 'powerpoint' || kind === 'powerpoint_rich' || kind === 'powerpoint-rich';
  const base = isDeck ? '/templates/powerpoint-rich' : '/templates';
  if (!templateId || templateId === 'new') return `${base}/new`;
  return `${base}/${encodeURIComponent(templateId)}/edit`;
}

/**
 * Open a template editor (existing route) in-app or in a new tab.
 * @param {Function} navigate - react-router `navigate`.
 * @param {{templateId?: string, kind?: string, newTab?: boolean, replace?: boolean}} options - Target ('new' creates).
 * @returns {string} The path that was opened.
 */
export function openTemplateEditor(navigate, { templateId = 'new', kind = 'document', newTab = false, replace = false } = {}) {
  const path = templateEditorPath({ templateId, kind });
  if (newTab) {
    openInNewTab(path);
    return path;
  }
  navigate(path, { replace });
  return path;
}

/**
 * Project page path, optionally on a tab.
 * @param {string} projectId - Project id.
 * @param {string} [tab] - 'sessions' | 'requirements' | 'documents' | ...
 * @param {Record<string, string>} [extra] - Extra query values (e.g. highlight).
 * @returns {string} Path.
 */
export function projectPath(projectId, tab, extra = {}) {
  return `/projects/${encodeURIComponent(projectId)}${buildQuery({ tab, ...extra })}`;
}

/**
 * Customer page path.
 * @param {string} customerId - Customer id.
 * @returns {string} Path.
 */
export function customerPath(customerId) {
  return `/customers/${encodeURIComponent(customerId)}`;
}

/**
 * Session (recording) hub path.
 * @param {string} sessionId - Session id.
 * @param {string} [tab] - Optional tab.
 * @returns {string} Path.
 */
export function sessionPath(sessionId, tab) {
  return `/customers/sessions/${encodeURIComponent(sessionId)}${buildQuery({ tab })}`;
}

/** Home page anchor of the "Record a meeting" card. */
export const RECORD_MEETING_ANCHOR = 'record-meeting';

/**
 * Go to the place where a meeting can be recorded (Home's record card).
 * @param {Function} navigate - react-router `navigate`.
 * @returns {void}
 */
export function goToRecordMeeting(navigate) {
  navigate(`/home#${RECORD_MEETING_ANCHOR}`);
}

/**
 * The document id when the route is a document editor (new or legacy form), else null.
 * @param {string} pathname - location.pathname.
 * @param {string} [search] - location.search.
 * @returns {string|null} Document id.
 */
export function parseDocumentEditorRoute(pathname, search = '') {
  const match = /^\/documents\/([^/]+)\/edit\/?$/.exec(pathname || '');
  if (match) {
    try {
      return decodeURIComponent(match[1]);
    } catch {
      return match[1];
    }
  }
  if (pathname === '/editor') return new URLSearchParams(search || '').get('documentId') || null;
  return null;
}

/**
 * Whether a route is an editing surface (document workspace or template editor).
 * @param {string} pathname - location.pathname.
 * @param {string} [search] - location.search.
 * @returns {boolean} True for editor routes.
 */
export function isEditorRoute(pathname, search = '') {
  if (parseDocumentEditorRoute(pathname, search)) return true;
  const parts = String(pathname || '').split('/').filter(Boolean);
  if (parts[0] !== 'templates') return false;
  return parts.includes('edit') || parts[parts.length - 1] === 'new';
}

/**
 * Where an old `/editor?...` link should go now.
 * - `documentId` -> the document workspace (other params are kept).
 * - `templateId` (no document) -> that template's editor; `new` (no ids) -> new template.
 * - `projectId` only -> the project's Documents tab.
 * - nothing -> the Documents list.
 * @param {string} search - location.search of the legacy URL.
 * @returns {string} Target path.
 */
export function legacyEditorRedirectTarget(search = '') {
  const params = new URLSearchParams(search || '');
  const documentId = params.get('documentId');
  if (documentId) {
    params.delete('documentId');
    const rest = params.toString();
    return `/documents/${encodeURIComponent(documentId)}/edit${rest ? `?${rest}` : ''}`;
  }
  const templateId = params.get('templateId');
  if (templateId) return templateEditorPath({ templateId, kind: params.get('kind') || 'document' });
  const projectId = params.get('projectId');
  if (projectId) return projectPath(projectId, 'documents');
  if (params.has('new')) return templateEditorPath({ templateId: 'new' });
  return '/documents';
}

/**
 * Normalize a `from` value saved by route guards into a safe in-app path.
 * @param {unknown} from - String path or a location-like object.
 * @returns {string|null} Path starting with '/', or null.
 */
export function safeReturnPath(from) {
  let path = null;
  if (typeof from === 'string') path = from;
  else if (from && typeof from === 'object' && typeof from.pathname === 'string') {
    path = `${from.pathname}${from.search || ''}${from.hash || ''}`;
  }
  if (!path || !path.startsWith('/') || path.startsWith('//')) return null;
  if (path === '/login' || path.startsWith('/login?')) return null;
  return path;
}


/** Signed-in user + workspace the recent documents list belongs to (null when signed out). */
let recentDocumentsScope = null;

/**
 * Scope the recent documents list to a user and workspace. App sets this from the auth state;
 * while no scope is set (signed out) the list reads empty and records nothing, so a shared
 * browser or another workspace never shows someone else's document titles.
 * Also removes the old unscoped list written by earlier versions.
 * @param {{userId?: string|null, accountId?: string|null}|null} scope - Current user and account.
 * @returns {void}
 */
export function setRecentDocumentsScope(scope) {
  const userId = scope?.userId || null;
  const accountId = scope?.accountId || null;
  const next = userId && accountId ? `${userId}:${accountId}` : null;
  if (next === recentDocumentsScope) return;
  recentDocumentsScope = next;
  try {
    window.localStorage.removeItem(RECENT_DOCUMENTS_STORAGE_KEY);
  } catch {
    /* storage unavailable */
  }
  try {
    window.dispatchEvent(new Event(RECENT_DOCUMENTS_EVENT));
  } catch {
    /* no window (tests outside jsdom) */
  }
}

/**
 * localStorage key of the current scope's recent documents list.
 * @returns {string|null} Key, or null when signed out.
 */
export function recentDocumentsKey() {
  return recentDocumentsScope ? `${RECENT_DOCUMENTS_STORAGE_KEY}:${recentDocumentsScope}` : null;
}

/**
 * Read the recent documents list of the current user and workspace.
 * @returns {Array<{id: string, name: string, projectId: string|null, openedAt: number}>} Most recent first.
 */
export function readRecentDocuments() {
  const key = recentDocumentsKey();
  if (!key) return [];
  try {
    const raw = window.localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((entry) => entry && typeof entry.id === 'string')
      .map((entry) => ({
        id: entry.id,
        name: typeof entry.name === 'string' && entry.name ? entry.name : 'Untitled document',
        projectId: typeof entry.projectId === 'string' ? entry.projectId : null,
        openedAt: Number(entry.openedAt) || 0,
      }))
      .slice(0, RECENT_DOCUMENTS_LIMIT);
  } catch {
    return [];
  }
}

/**
 * Write the current scope's list and tell listeners.
 * @param {Array<object>} entries - Entries.
 * @returns {void}
 */
function writeRecentDocuments(entries) {
  const key = recentDocumentsKey();
  if (!key) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(entries));
    window.dispatchEvent(new Event(RECENT_DOCUMENTS_EVENT));
  } catch {
    /* storage unavailable: the list is a convenience only */
  }
}

/**
 * Put a document at the top of the recent documents list (no-op while signed out).
 * @param {{id: string, name?: string, projectId?: string|null}} doc - Document.
 * @returns {void}
 */
export function recordRecentDocument({ id, name, projectId = null }) {
  if (!id || !recentDocumentsKey()) return;
  const existing = readRecentDocuments().filter((entry) => entry.id !== id);
  writeRecentDocuments(
    [{ id, name: name || 'Untitled document', projectId: projectId || null, openedAt: Date.now() }, ...existing].slice(0, RECENT_DOCUMENTS_LIMIT)
  );
}

/**
 * Remove a document from the recent list (e.g. after it was deleted or failed to load).
 * @param {string} id - Document id.
 * @returns {void}
 */
export function forgetRecentDocument(id) {
  if (!recentDocumentsKey()) return;
  writeRecentDocuments(readRecentDocuments().filter((entry) => entry.id !== id));
}

/** Pages with unsaved edits (e.g. the document editor) register a flush here. */
const unsavedWorkFlushers = new Set();

/**
 * Register a function that saves pending edits. Called before actions that unmount the page
 * without a router navigation (logging out), so they can save first.
 * @param {() => Promise<boolean>|boolean} flush - Saves pending work; resolves false when it could not.
 * @returns {() => void} Unregister.
 */
export function registerUnsavedWorkFlusher(flush) {
  if (typeof flush !== 'function') return () => {};
  unsavedWorkFlushers.add(flush);
  return () => unsavedWorkFlushers.delete(flush);
}

/**
 * Save every registered page's pending edits.
 * @param {{timeoutMs?: number}} [options] - Give up (and report failure) after this long.
 * @returns {Promise<boolean>} True when everything saved (or nothing was pending).
 */
export async function flushUnsavedWork({ timeoutMs = 10000 } = {}) {
  if (!unsavedWorkFlushers.size) return true;
  const all = Promise.all(
    [...unsavedWorkFlushers].map(async (flush) => {
      try {
        return (await flush()) !== false;
      } catch {
        return false;
      }
    })
  ).then((results) => results.every(Boolean));
  let timer = null;
  const timeout = new Promise((resolve) => {
    timer = setTimeout(() => resolve(false), timeoutMs);
  });
  try {
    return await Promise.race([all, timeout]);
  } finally {
    clearTimeout(timer);
  }
}

/** Window event that opens the shell's quick-create dialogs (QuickCreateHost). */
export const QUICK_CREATE_EVENT = 'quillai:quick-create';

/**
 * Open a quick-create dialog from anywhere in the signed-in app: a new project (optionally for
 * a customer), a new customer, or a new template (checks the plan's template limit first).
 * After creating, the dialog opens the new item.
 * @param {'project'|'customer'|'template'} kind - What to create.
 * @param {{customerId?: string, templateKind?: 'document'|'powerpoint-rich'}} [options] - Prefill.
 * @returns {void}
 */
export function requestQuickCreate(kind, options = {}) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(QUICK_CREATE_EVENT, { detail: { kind, ...options } }));
}
